'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useProductListByIds } from '@/hooks/react-query/products/use-product'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { formatPrice } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { ImageWithFallback as Image } from '@/components/common/image-with-fallback'
import Link from 'next/link'

type Props = {
  thumbnail: string
  productIds: string[]
  isVideo?: boolean
}

export const CollectionMixMatchItem: React.FC<Props> = ({
  thumbnail,
  productIds,
  isVideo,
}) => {
  const t = useTranslations()
  return (
    <ResponsiveModal>
      <ResponsiveModalTrigger asChild>
        <div className='group relative cursor-pointer overflow-hidden rounded-2xl'>
          {isVideo ? (
            <video
              className='aspect-[435/596] w-full object-cover'
              loop
              playsInline
              muted
              autoPlay
            >
              <source src={thumbnail} type='video/mp4' />
            </video>
          ) : (
            <Image
              src={ImageHelper.getImageUrlCdn(thumbnail)}
              alt={'mix max 1'}
              width={435}
              height={596}
              loading='lazy'
              className='aspect-[435/596] w-full object-cover transition-transform duration-300 group-hover:scale-105'
              unoptimized
            />
          )}
          {/* <Image
            src={ImageHelper.getImageUrlCdn('/image/October2024/mceclip0_132.png')}
            alt={'icon cart'}
            width={60}
            height={60}
            loading='lazy'
            className='absolute right-3 top-3 h-9 w-9 object-cover md:h-13 md:w-13'
          /> */}
          <Button
            variant='white'
            size={'large'}
            className='absolute bottom-4 left-3 max-lg:h-8 max-lg:px-6 max-lg:text-xs lg:bottom-6 lg:left-4'
          >
            {t('bra_legging_collection_hardcode.voucher.buy_now')}
          </Button>
        </div>
      </ResponsiveModalTrigger>
      <ResponsiveModalContent
        className='gap-0 border-none px-5 py-6 lg:h-[600px] lg:max-h-none lg:w-[1000px] lg:max-w-none lg:rounded-md lg:px-9 lg:py-10'
        aria-describedby={'modal-login'}
      >
        <ResponsiveModalTitle className='hidden' />
        <ResponsiveModalDescription className='hidden' />
        <PreviewProductsModalContent
          productIds={productIds}
          thumbnail={thumbnail}
          isVideo={isVideo}
        />
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}

const PreviewProductsModalContent: React.FC<{
  productIds: string[]
  thumbnail: string
  isVideo?: boolean
}> = ({ productIds, thumbnail, isVideo }) => {
  const { data: products, isLoading } = useProductListByIds(productIds, 3)
  const t = useTranslations()
  return (
    <div className='flex h-full flex-col gap-3 lg:flex-row lg:gap-6'>
      {isVideo ? (
        <video
          className='h-full rounded-2xl max-md:hidden'
          loop
          playsInline
          muted
          autoPlay
        >
          <source src={thumbnail} type='video/mp4' />
        </video>
      ) : (
        <picture className='max-lg:basis-2/5 max-md:hidden'>
          {' '}
          <Image
            src={ImageHelper.getImageUrlCdn(thumbnail)}
            alt={'mix match item'}
            width={388}
            height={520}
            loading='lazy'
            className='h-full rounded-2xl object-cover object-top max-lg:h-48 max-lg:w-full max-lg:object-top'
          />
        </picture>
      )}
      {isLoading ? (
        <ProductListSkeleton />
      ) : (
        <ul className='no-scrollbar max-h-[320px] space-y-5 overflow-y-auto pr-1 max-lg:flex-1 lg:max-h-[520px]'>
          {products?.map((product) => {
            return (
              <li
                className='flex items-stretch gap-3 lg:gap-6'
                key={product.seo_alias}
              >
                <Link href={`/product/${product.seo_alias}`} className='block'>
                  <Image
                    src={ImageHelper.getImageUrlCdn(product.default_thumbnail)}
                    alt={product.title}
                    width={120}
                    height={160}
                    loading='lazy'
                    className='h-full min-w-20 rounded-lg object-cover max-lg:h-28 max-lg:w-20 max-lg:object-top'
                  />
                </Link>
                <div className='flex flex-1 flex-col justify-between'>
                  <div>
                    <Link
                      href={`/product/${product.seo_alias}`}
                      className='line-clamp-2 font-sans text-sm font-medium text-neutral-900 hover:text-primary lg:text-xl'
                    >
                      {product.title}
                    </Link>
                    <div className='mt-2 lg:mt-4'>
                      {product.compare_price &&
                      product.compare_price !== product.regular_price ? (
                        <del className='mb-0.5 font-sans text-xs leading-none text-neutral-400 lg:mb-2 lg:text-base'>
                          {formatPrice(product.compare_price ?? 0)}
                        </del>
                      ) : null}
                      <span className='flex items-center gap-2 font-sans text-sm font-medium leading-none text-neutral-900 lg:text-2xl'>
                        {formatPrice(product.regular_price ?? 0)}
                        {product.sale_percent ? (
                          <TagThumbnail
                            title={`-${product.sale_percent}%`}
                            className='max-lg:h-4 max-lg:px-1.5'
                          />
                        ) : null}
                      </span>
                    </div>
                  </div>
                  <div className='mt-3 flex justify-end'>
                    <Button
                      variant={'primary'}
                      asChild
                      className='max-lg:h-8 max-lg:text-xs'
                    >
                      <Link
                        href={`/product/${product.seo_alias}`}
                        className='ml-auto items-center gap-2'
                      >
                        {t('bra_legging_collection_hardcode.voucher.buy_now')}
                        <Image
                          src={'/icons/icon-arrow-line-right.svg'}
                          width={16}
                          height={10}
                          alt='icon arrow right'
                        />
                      </Link>
                    </Button>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

const ProductListSkeleton = () => {
  // Giả sử products có khoảng 4 item skeleton để hiển thị
  const skeletonItems = Array.from({ length: 2 })

  return (
    <div className='flex h-full flex-col gap-3 lg:flex-row lg:gap-6'>
      {/* Skeleton danh sách sản phẩm bên phải */}
      <ul className='no-scrollbar max-h-[320px] space-y-5 overflow-y-auto pr-1 max-lg:flex-1 lg:max-h-[520px]'>
        {skeletonItems.map((_, idx) => (
          <li
            key={idx}
            className='flex animate-pulse items-stretch gap-3 lg:gap-6'
          >
            {/* Thumbnail nhỏ */}
            <div className='h-[160px] min-w-20 rounded-lg bg-gray-300 max-lg:h-28 max-lg:w-20' />

            <div className='flex flex-1 flex-col justify-between'>
              <div>
                {/* Tiêu đề */}
                <div className='mb-2 h-6 w-3/4 rounded bg-gray-300 lg:h-8'></div>

                {/* Giá cũ (khoảng cách nếu có) */}
                <div className='mb-1 h-4 w-1/4 rounded bg-gray-300'></div>

                {/* Giá mới + tag sale */}
                <div className='flex items-center gap-2'>
                  <div className='h-6 w-1/3 rounded bg-gray-300 lg:h-10'></div>
                  <div className='h-4 w-12 rounded bg-gray-300 max-lg:h-5'></div>
                </div>
              </div>

              {/* Button Mua ngay */}
              <div className='mt-3 flex justify-end'>
                <div className='h-8 w-24 rounded bg-gray-300 max-lg:h-8'></div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
